"use client"

import React, { useState, useEffect, useCallback, useRef, useContext, createContext } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { User, LoginResponse } from '@/types'
import { authApi } from '@/lib/app-api'

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<any>
  logout: () => Promise<void>
  refreshToken: () => Promise<any>
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const loggedRoutesRef = useRef<Set<string>>(new Set())
  const [user, setUser] = useState<User | null>(null)
  const [isMounted, setIsMounted] = useState(false)
  const [hasToken, setHasToken] = useState(false)
  const [authVerified, setAuthVerified] = useState(false)
  const router = useRouter()
  const pathname = usePathname()

  const isAuthRoute =
    pathname.startsWith('/login') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/reset-password')

  // Derive isLoading - only show loading when we have a token but haven't verified yet
  const isLoading = isMounted && hasToken && !authVerified

  // Set mounted state after hydration to prevent localStorage access during SSR
  useEffect(() => {
    setIsMounted(true)
    const token = !!localStorage.getItem('accessToken')
    setHasToken(token)
  }, [])

  // Validate token and fetch user data when we have a token
  useEffect(() => {
    if (!isMounted || !hasToken) return
    
    // Don't refetch if already verified
    if (authVerified && user) return
    
    console.log('[useAuth] Fetching user data, authVerified:', authVerified)
    
    authApi.getCurrentUser()
      .then((userData) => {
        console.log('[useAuth] User data fetched successfully')
        // Fetch permissions
        return authApi.getUserPermissions()
          .then((permissionsData) => {
            // Handle both possible response formats
            const permissionsList = 'permissions' in permissionsData 
              ? (permissionsData as any).permissions
              : (permissionsData as any).effective_permissions || []

            const transformedUser = {
              id: userData.id,
              email: userData.email,
              name: userData.name,
              first_name: userData.first_name,
              last_name: userData.last_name,
              role: userData.role,
              status: userData.status,
              avatar: userData.avatar,
              phone_number: userData.phone_number,
              is_active: userData.is_active,
              created_at: userData.created_at,
              updated_at: userData.updated_at,
              permissions: permissionsList
            }
            
            console.log('[useAuth] Setting user:', transformedUser.email)
            setUser(transformedUser)
            setAuthVerified(true)
          })
      })
      .catch((error) => {
        console.error('[useAuth] Failed to fetch user:', error)
        // Token is invalid, clear everything
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        setHasToken(false)
        setUser(null)
        setAuthVerified(false)
      })
  }, [isMounted, hasToken, authVerified, user])

  const login = useCallback(async (email: string, password: string, rememberMe = false): Promise<any> => {
    try {
      console.log('[useAuth] Starting login for:', email)
      
      const response: LoginResponse = await authApi.login({ email, password, rememberMe })
      
      console.log('[useAuth] Login response received:', {
        hasAccessToken: !!response.access_token,
        hasRefreshToken: !!response.refresh_token,
        tokenType: response.token_type,
        expiresIn: response.expires_in
      })

      // Store tokens in localStorage
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined' && response.access_token) {
        localStorage.setItem('accessToken', response.access_token)
        console.log('[useAuth] Saved accessToken to localStorage, length:', response.access_token.length)
        
        if (response.refresh_token) {
          localStorage.setItem('refreshToken', response.refresh_token)
          console.log('[useAuth] Saved refreshToken to localStorage')
        }
        
        // Verify token was saved
        const savedToken = localStorage.getItem('accessToken')
        console.log('[useAuth] Verified accessToken in localStorage:', !!savedToken)
      } else {
        console.error('[useAuth] No access_token in login response!')
      }

      // Clear existing state and set token
      setUser(null)
      setAuthVerified(false)
      setHasToken(true)
      console.log('[useAuth] Auth state updated - hasToken: true')

      return response
    } catch (error) {
      console.error('[useAuth] Login failed:', error)
      throw error
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear all authentication data from localStorage
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        localStorage.removeItem('permissions')
      }

      // Clear sessionStorage as well for completeness
      if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
        sessionStorage.clear()
      }

      setUser(null)
      setAuthVerified(false)
      setHasToken(false)
      router.push('/login')
    }
  }, [router])

  const refreshToken = useCallback(async () => {
    try {
      const response = await authApi.refreshToken()
      // Update token in localStorage
      if (response.access_token) {
        localStorage.setItem('accessToken', response.access_token)
      }
      if (response.refresh_token) {
        localStorage.setItem('refreshToken', response.refresh_token)
      }
      // Re-verify auth
      setAuthVerified(false)
      setHasToken(true)
      return response
    } catch (error) {
      // If refresh fails, logout user
      setUser(null)
      setAuthVerified(false)
      setHasToken(false)
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      router.push('/login')
      throw error
    }
  }, [router])

  // Simplified logging for debugging
  useEffect(() => {
    if (!loggedRoutesRef.current.has(pathname)) {
      loggedRoutesRef.current.add(pathname)
      console.log('[useAuth] route', {
        pathname,
        hasUser: !!user,
        isLoading,
        hasToken,
        authVerified
      })
    }
  }, [pathname, user, isLoading, hasToken, authVerified])

  const checkAuth = useCallback(async () => {
    // Force re-verification
    setAuthVerified(false)
  }, [])

  useEffect(() => {
    // Skip authentication check for auth routes
    if (isAuthRoute) {
      if (!hasToken) {
        setUser(null)
        setAuthVerified(false)
      }
      return
    }

    // Check auth status based on token presence
    if (!hasToken) {
      setUser(null)
      setAuthVerified(false)
    }
  }, [pathname, isAuthRoute, hasToken])

  const value: AuthContextValue = {
    user,
    isLoading,
    login,
    logout,
    refreshToken,
    checkAuth,
  }

  return React.createElement(AuthContext.Provider, { value }, children)
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    // Fallback to avoid hard crash if provider is not mounted (e.g., stale bundle)
    return {
      user: null,
      isLoading: false,
      login: async () => undefined,
      logout: async () => undefined,
      refreshToken: async () => undefined,
      checkAuth: async () => undefined,
    }
  }
  return context
}
